package com.ijoin.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.ijoin.data.model.LegalDocument
import com.ijoin.data.model.LegalDocumentType
import com.ijoin.data.repository.LegalRepository
import com.ijoin.i18n.strings
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionHeader
import com.ijoin.ui.design.EventSelectableCard
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.eventBackgroundBrush
import kotlinx.coroutines.launch

/**
 * Full-screen modal for accepting updated legal documents.
 * Non-dismissible - user must accept to continue using the app.
 */
@Composable
fun LegalAcceptanceModal(
    pendingDocuments: List<String>,
    onAccepted: () -> Unit,
    legalRepository: LegalRepository = remember { LegalRepository() }
) {
    val strings = strings()
    val scope = rememberCoroutineScope()

    var accepted by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Document viewing state
    var viewingDocument by remember { mutableStateOf<String?>(null) }
    var currentDocument by remember { mutableStateOf<LegalDocument?>(null) }
    var isLoadingDocument by remember { mutableStateOf(false) }
    var documentError by remember { mutableStateOf<String?>(null) }

    Dialog(
        onDismissRequest = { /* Non-dismissible */ },
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
            usePlatformDefaultWidth = false
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Spacer(modifier = Modifier.height(32.dp))

                EventSurfaceCard(
                    modifier = Modifier
                        .fillMaxWidth()
                        .widthIn(max = 520.dp),
                    cornerRadius = EventCardCompactCorner,
                    backgroundColor = EventCardSurface,
                    borderColor = EventCardOutline,
                    contentPadding = PaddingValues(20.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    EventSectionHeader(
                        title = strings.legalDocumentsUpdated,
                        subtitle = strings.legalDocumentsUpdatedDescription
                    )

                    if (pendingDocuments.isNotEmpty()) {
                        Text(
                            text = strings.iAgreeToThe,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = EventCardAccentDark
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        pendingDocuments.forEach { docType ->
                            val displayName = when (docType) {
                                "TERMS_OF_SERVICE" -> strings.termsOfService
                                "PRIVACY_POLICY" -> strings.privacyPolicy
                                "REFUND_POLICY" -> strings.refundPolicy
                                "ORGANIZER_TERMS" -> strings.organizerTerms
                                "AI_DATA_CONSENT" -> strings.aiDataConsent
                                else -> docType
                            }

                            EventSelectableCard(
                                selected = false,
                                onClick = {
                                    viewingDocument = docType
                                    isLoadingDocument = true
                                    documentError = null
                                    currentDocument = null
                                    scope.launch {
                                        legalRepository.getDocument(docType)
                                            .onSuccess { doc ->
                                                currentDocument = doc
                                                isLoadingDocument = false
                                            }
                                            .onFailure { e ->
                                                documentError = e.message ?: strings.legalDocumentLoadError
                                                isLoadingDocument = false
                                            }
                                    }
                                }
                            ) {
                                Column(
                                    modifier = Modifier.weight(1f),
                                    verticalArrangement = Arrangement.spacedBy(4.dp)
                                ) {
                                    Text(
                                        text = displayName,
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                    Text(
                                        text = strings.viewDocument,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                                Icon(
                                    imageVector = Icons.Default.ChevronRight,
                                    contentDescription = strings.viewDocument,
                                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    }

                    EventSelectableCard(
                        selected = accepted,
                        onClick = { if (!isLoading) accepted = !accepted },
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp)
                    ) {
                        Text(
                            text = strings.iAcceptUpdatedTerms,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.weight(1f)
                        )
                        Checkbox(
                            checked = accepted,
                            onCheckedChange = null,
                            enabled = !isLoading
                        )
                    }

                    if (error != null) {
                        Text(
                            text = error.orEmpty(),
                            color = EventCardAlert,
                            style = MaterialTheme.typography.bodySmall,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }

                    EventPrimaryButton(
                        text = strings.continueButton,
                        onClick = {
                            isLoading = true
                            error = null
                            scope.launch {
                                // Convert string types to LegalDocumentType
                                val types = pendingDocuments.mapNotNull { typeName ->
                                    LegalDocumentType.fromApiName(typeName)
                                }

                                if (types.isNotEmpty()) {
                                    legalRepository.acceptDocuments(types)
                                        .onSuccess {
                                            isLoading = false
                                            onAccepted()
                                        }
                                        .onFailure { e ->
                                            isLoading = false
                                            error = e.message ?: strings.unknownErrorMessage
                                        }
                                } else {
                                    isLoading = false
                                    onAccepted()
                                }
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(52.dp),
                        enabled = accepted && !isLoading,
                        isLoading = isLoading
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))
            }
        }
    }

    // Document viewing dialog
    if (viewingDocument != null) {
        LegalDocumentDialog(
            document = currentDocument,
            isLoading = isLoadingDocument,
            error = documentError,
            onDismiss = {
                viewingDocument = null
                currentDocument = null
                documentError = null
            }
        )
    }
}
